import React, { useState, useEffect } from 'react'
import { Col, Row, Modal, Button, Form, Input, InputNumber, Select, Checkbox, Tooltip } from 'antd'
import { QuestionCircleOutlined } from "@ant-design/icons";
import { verifyCreditAccount } from '../../CreditReportV2/creditReportV2Utils'
import './CreditAccountModal.css'

const CreditAccountModal = ({
    open,
    mode = 'personal',
    handleType,
    activeTab,
    initialValues,
    onFinish,
    onCancel,
}) => {
    const [creditAccountDisabled, setCreditAccountDisabled] = useState(true)
    const [testing, setTesting] = useState(false)
    const [testResult, setTestResult] = useState(null)
    const [form] = Form.useForm()

    // Ask the vendor whether this login works before it is saved. Without this the first
    // proof that a credential is wrong arrives mid-pull, on a real borrower.
    const testConnection = async () => {
        const values = form.getFieldsValue()
        setTesting(true)
        setTestResult(null)
        try {
            setTestResult(await verifyCreditAccount({
                Vendor: values.Vendor,
                Username: values.Username,
                Password: values.Password,
            }))
        } catch (err) {
            setTestResult({ ok: false, message: err.message || 'Could not reach the credit vendor.' })
        } finally {
            setTesting(false)
        }
    }
    const validateMessages = {
        required: '${label} is required!',
        types: {
            email: '${label} is not a valid email!',
        }
    }
    const vendorsList = {
        'KCBCredit': 'KCB Credit',
        'AdvantageCredit': 'Advantage Credit',
        'CreditTechnology': 'Credit Technology',
        'CreditTechnologies': 'Credit Technologies',
        'PremiumCreditBureau': 'Premium Credit Bureau',
        'PremiumCreditDataCDC': 'Premium Credit Data (CDC)',
        'Xactus': 'Xactus',
    }
    const accountType = initialValues?.Type || activeTab || 'SoftPull'
    const showPricing = mode === 'team' && accountType === 'HardPull'
    const isEdit = handleType === 'edit' || Boolean(initialValues?.Username && initialValues?.Vendor && mode === 'team' && handleType !== 'add')

    const checkFormComplete = () => {
        // Any edit invalidates an earlier test — never leave a stale "accepted" on screen
        // next to credentials that have since changed.
        setTestResult(null)
        const values = form.getFieldsValue()
        if (Object.keys(values).length === 0) {
            return true
        }
        const requiredFields = ['Vendor', 'Username', 'Password']
        const allFieldsFilled = requiredFields.every(field => values[field])
        setCreditAccountDisabled(!allFieldsFilled)
    }
    const modalConfirm = (formValues) => {
        const payload = { ...formValues }
        // Trim the user name: a stray space is invisible here but is sent verbatim in the
        // vendor's Basic-auth header, so it reads to them as a different account.
        // The password is left untouched — only the user typed it and it may legitimately
        // contain spaces.
        if (typeof payload.Username === 'string') {
            payload.Username = payload.Username.trim()
        }
        if (showPricing) {
            const individual = Number(formValues.IndividualTriMergePrice)
            const joint = Number(formValues.JointTriMergePrice)
            payload.IndividualTriMergePrice = Number.isFinite(individual) ? individual : 0
            payload.JointTriMergePrice = Number.isFinite(joint) ? joint : 0
            if (payload.IndividualTriMergePrice <= 0 || payload.JointTriMergePrice <= 0) {
                Modal.confirm({
                    title: "Save $0 pricing?",
                    content:
                        "Borrower-paid pulls require individual and joint prices greater than $0. Saving $0 keeps borrower-paid unavailable, and turns it off if it is currently enabled. Continue?",
                    okText: "Save",
                    cancelText: "Cancel",
                    onOk: () => {
                        onFinish(payload)
                        modalCancel()
                    },
                })
                return
            }
        }
        onFinish(payload)
        modalCancel()
    }
    const modalCancel = () => {
        setCreditAccountDisabled(true)
        form.resetFields()
        onCancel()
    }
    useEffect(() => {
        const toPrice = (value) => {
            if (value === null || value === undefined || value === "") return undefined
            const n = Number(value)
            return Number.isFinite(n) ? n : undefined
        }
        form.setFieldsValue({
            ...initialValues,
            IndividualTriMergePrice: toPrice(initialValues?.IndividualTriMergePrice),
            JointTriMergePrice: toPrice(initialValues?.JointTriMergePrice),
        })
        checkFormComplete()
    }, [initialValues, form, open])
    return (
        <Modal
            className="credit-account-modal"
            open={open}
            title={isEdit ? 'Edit credit account' : 'Connect a credit account'}
            footer={null}
            onCancel={modalCancel}
            width={showPricing ? 720 : 520}
        >
            <Form
                form={form}
                layout='vertical'
                requiredMark={false}
                validateMessages={validateMessages}
                initialValues={initialValues}
                onFieldsChange={checkFormComplete}
                onFinish={(value) => modalConfirm(value)}
            >
                <div style={{padding: '22px 28px 20px'}}>
                    <Row>
                        <Col span={24}>
                            <Form.Item
                                className="credit-account-form-item"
                                name="Vendor"
                                label="Credit vendor"
                                rules={[{ required: true }]}
                            >
                                <Select
                                    style={{ height: '50px' }}
                                    className="credit-vendor-select"
                                    options={
                                        Object.keys(vendorsList).map((item) => {
                                            return { label: vendorsList[item], value: item }
                                        })
                                    }
                                    placeholder="Select"
                                />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={16}>
                        <Col span={showPricing ? 12 : 24}>
                            <Form.Item
                                className="credit-account-form-item"
                                name="Username"
                                label="User name"
                                rules={[{ required: true }]}
                                style={{ marginBottom: showPricing ? 26 : undefined }}
                            >
                                <Input style={{ height: '50px' }} className="credit-vendor-input" placeholder="Enter your user name" />
                            </Form.Item>
                        </Col>
                        <Col span={showPricing ? 12 : 24}>
                            <Form.Item
                                className="credit-account-form-item"
                                style={{ marginBottom: showPricing ? 26 : 20 }}
                                name="Password"
                                label="Password"
                                rules={[{ required: true }]}
                            >
                                <Input.Password style={{ height: '50px' }} className="credit-vendor-input" placeholder="Enter your password" />
                            </Form.Item>
                        </Col>
                    </Row>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
                        <Button onClick={testConnection} loading={testing} disabled={creditAccountDisabled}>
                            Test connection
                        </Button>
                        {testResult && (
                            <span style={{ color: testResult.ok ? '#138D2E' : '#CB2E2E', fontSize: 13 }}>
                                {testResult.ok
                                    ? 'Credentials accepted by the credit vendor.'
                                    : testResult.message}
                            </span>
                        )}
                    </div>
                    {showPricing && (
                        <div className="credit-report-pricing-section">
                            <div className="credit-report-pricing-title">Credit report pricing</div>
                            <div className="credit-report-pricing-hint">
                                Ask your CRA for this account&apos;s rate sheet. Prices greater than $0 enable borrower-paid pulls on this account — confirm with your CRA that card payment is enabled first. $0 is allowed but borrower-paid stays unavailable.
                            </div>
                            <div className="credit-report-pricing-row">
                                <div className="credit-report-pricing-label">Individual report (tri-merge)</div>
                                <div className="credit-report-pricing-field">
                                    <Form.Item name="IndividualTriMergePrice" noStyle>
                                        <InputNumber
                                            className="credit-vendor-price-input"
                                            style={{ width: 180 }}
                                            controls={false}
                                            min={0}
                                            precision={2}
                                            prefix="$"
                                            placeholder="0.00"
                                        />
                                    </Form.Item>
                                </div>
                            </div>
                            <div className="credit-report-pricing-row">
                                <div className="credit-report-pricing-label">Joint report (tri-merge)</div>
                                <div className="credit-report-pricing-field">
                                    <Form.Item name="JointTriMergePrice" noStyle>
                                        <InputNumber
                                            className="credit-vendor-price-input"
                                            style={{ width: 180 }}
                                            controls={false}
                                            min={0}
                                            precision={2}
                                            prefix="$"
                                            placeholder="0.00"
                                        />
                                    </Form.Item>
                                </div>
                                <span className="credit-report-pricing-extra">charged once, covers both applicants</span>
                            </div>
                        </div>
                    )}
                    {
                        mode === 'personal' && (
                            <>
                                <Row>
                                    <Col span={24}>
                                        <Form.Item
                                            style={{ marginBottom: 8 }}
                                            name="IsDefault"
                                            valuePropName="checked"
                                        >
                                            <Checkbox className='credit-account-checkbox'>
                                                <div style={{display: 'flex', alignItems: 'center'}}>
                                                    <div>Set as the default credit account</div>
                                                    <Tooltip title="When selected, this account will be used as the primary account for running credit pulls by default. You can change this later in settings.">
                                                        <div style={{width: 24, height: 24, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#8A8C90', marginLeft: 2}}><QuestionCircleOutlined /></div>
                                                    </Tooltip>
                                                </div>
                                            </Checkbox>
                                        </Form.Item>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col span={24}>
                                        <Form.Item
                                            name="Type"
                                            initialValue={activeTab === 'SoftPull' ? 'SoftPull' : 'HardPull'}
                                            hidden
                                        >
                                            <Input />
                                        </Form.Item>
                                        {handleType === 'add' && (
                                            <Form.Item
                                                style={{ marginBottom: 0 }}
                                                name="typeSwitch"
                                                valuePropName="checked"
                                                getValueProps={() => ({
                                                    checked: form.getFieldValue('Type') === (activeTab === 'SoftPull' ? 'HardPull' : 'SoftPull')
                                                })}
                                                getValueFromEvent={(e) => {
                                                    const newType = e.target.checked
                                                        ? (activeTab === 'SoftPull' ? 'HardPull' : 'SoftPull')
                                                        : (activeTab === 'SoftPull' ? 'SoftPull' : 'HardPull');
                                                    form.setFieldValue('Type', newType);
                                                    return e.target.checked;
                                                }}
                                            >
                                                <Checkbox className='credit-account-checkbox'>
                                                    {activeTab === 'SoftPull' ? 'Use as hard credit check account' : 'Use as soft credit check account'}
                                                </Checkbox>
                                            </Form.Item>
                                        )}
                                    </Col>
                                </Row>
                            </>
                        )
                    }
                </div>
                <div style={{ height: '72px', padding: '0 28px', display: "flex", justifyContent: 'space-between', alignItems: 'center', borderTop: '1px solid #ECECEC' }}>
                    <Button className="credit-account-btn credit-account-cancel" type="link" onClick={modalCancel}>Cancel</Button>
                    <Button className="credit-account-btn credit-account-save" disabled={creditAccountDisabled} type='primary' htmlType="submit">
                        {isEdit ? 'Save' : 'Connect'}
                    </Button>
                </div>
            </Form>
        </Modal>
    )
}

export default CreditAccountModal
